<script setup lang="ts">
import type { TenantContext } from '~/types/types'
import type { SiteLayout } from '~/constants/siteConfig'

/**
 * Tenant home page — hero (per configured layout) followed by the shared
 * content sections. This is the "landing" menu entry; additional pages
 * arrive with the site builder phases.
 *
 * The hero layouts live in the shared site-builder layer and take a
 * decoupled `hero: SiteHeroContent` shape — we adapt this tenant's landing
 * model onto it here, and inject the app's stats markup via the #stats slot.
 */
const props = defineProps<{ tenant: TenantContext }>()

const { t } = useI18n()

// Layer hero layouts, resolved by auto-import name.
const heroes: Record<SiteLayout, string> = {
  split: 'SiteLayoutsSplitHero',
  centered: 'SiteLayoutsCenteredHero',
  editorial: 'SiteLayoutsEditorialHero'
}
const heroComponent = computed(() => heroes[props.tenant.site.layout] ?? 'SiteLayoutsCenteredHero')

const landing = computed(() => props.tenant.landing)

// Adapter: tenant/landing model → the layer's SiteHeroContent contract.
const heroContent = computed(() => ({
  eyebrow: props.tenant.name,
  headline: landing.value.hero_headline || props.tenant.name,
  subheadline: landing.value.hero_subheadline || props.tenant.description || undefined,
  cta_text: landing.value.hero_cta_text || undefined,
  cta_url: landing.value.hero_cta_url || undefined,
  secondary_cta_text: t('site.send_inquiry'),
  secondary_cta_url: '#contact',
  image_url: landing.value.hero_image_url || undefined,
  monogram: (props.tenant.name || '?').trim().charAt(0).toUpperCase(),
  animation: props.tenant.site.hero_animation
}))
</script>

<template>
  <article>
    <component
      :is="heroComponent"
      :hero="heroContent"
    >
      <template #stats>
        <LandingSectionsStats
          :landing="landing"
          :branding="tenant.branding"
          variant="row"
        />
      </template>
    </component>

    <LandingSectionsAbout
      :landing="landing"
      :variant="tenant.site.layout === 'editorial' ? 'stack' : 'side'"
      display-class="site-display"
    />
    <LandingSectionsVideo
      :landing="landing"
      display-class="site-display"
    />
    <LandingSectionsContentSections
      :landing="landing"
      :branding="tenant.branding"
      variant="alternating"
      display-class="site-display"
    />
    <LandingBookableStaffSection :primary-color="tenant.branding.primary_color" />
    <LandingSectionsFaq
      :landing="landing"
      :variant="tenant.site.layout === 'editorial' ? 'ruled' : 'boxed'"
      display-class="site-display"
    />
    <LandingSectionsCtaFooter
      :landing="landing"
      :branding="tenant.branding"
      variant="accent"
      display-class="site-display"
    />
    <LandingSectionsContactBlock :tenant-name="tenant.name" />
    <LandingSectionsSocials :landing="landing" />
  </article>
</template>
